// apps/web/src/lib/crypto.ts

import { apiClient } from '@/lib/api/client';

interface PublicKeyResponse {
  publicKey: string;
  algorithm: string;
  keyLength: number;
}

let cachedPublicKey: string | null = null;

/**
 * 获取 RSA 公钥
 */
export async function getPublicKey(): Promise<string> {
  if (cachedPublicKey) {
    return cachedPublicKey;
  }

  const result = await apiClient.get<PublicKeyResponse>('auth/public-key');
  cachedPublicKey = result.publicKey;
  return cachedPublicKey;
}

export interface EncryptedPasswordResult {
  password: string;
  encrypted: boolean;
}

/**
 * 使用 RSA-OAEP 加密密码
 * @param password 明文密码
 * @returns 加密后的密码和是否加密的标志
 */
export async function encryptPassword(password: string): Promise<EncryptedPasswordResult> {
  if (!crypto.subtle) {
    // crypto.subtle only available in secure contexts (HTTPS or localhost)
    // Fall back to sending plaintext; rely on HTTPS in production
    console.warn('Web Crypto API not available (non-secure context). Sending password without encryption.');
    return { password, encrypted: false };
  }

  const publicKeyPem = await getPublicKey();

  // 将 PEM 转换为 ArrayBuffer
  const publicKeyBuffer = pemToArrayBuffer(publicKeyPem);

  // 导入公钥
  const publicKey = await crypto.subtle.importKey(
    'spki',
    publicKeyBuffer,
    {
      name: 'RSA-OAEP',
      hash: 'SHA-256',
    },
    false,
    ['encrypt'],
  );

  // 编码密码为 UTF-8
  const encoder = new TextEncoder();
  const passwordBuffer = encoder.encode(password);

  // 加密
  const encryptedBuffer = await crypto.subtle.encrypt(
    { name: 'RSA-OAEP' },
    publicKey,
    passwordBuffer,
  );

  // 转换为 Base64
  return { password: arrayBufferToBase64(encryptedBuffer), encrypted: true };
}

/**
 * 清除缓存的公钥（用于强制重新获取）
 */
export function clearCachedPublicKey(): void {
  cachedPublicKey = null;
}

// 辅助函数：PEM 转 ArrayBuffer
function pemToArrayBuffer(pem: string): ArrayBuffer {
  const pemContent = pem
    .replace(/-----BEGIN.*KEY-----/, '')
    .replace(/-----END.*KEY-----/, '')
    .replace(/\s/g, '');

  const binary = atob(pemContent);
  const buffer = new ArrayBuffer(binary.length);
  const view = new Uint8Array(buffer);
  for (let i = 0; i < binary.length; i++) {
    view[i] = binary.charCodeAt(i);
  }
  return buffer;
}

// 辅助函数：ArrayBuffer 转 Base64
function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}
